import React, { createContext, useContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Only report unexpected errors to Sentry — 401 is expected for expired tokens
        if (!error?.response || error.response.status !== 401) {
          Sentry.captureException(error, { tags: { context: 'auth_check' } });
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);

      return userData;
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  const register = async (data) => {
    try {
      const response = await authAPI.register(data);
      const { access_token, user: userData } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);

      return userData;
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Ignore errors — token may already be expired
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Check if user has one of the specified roles
  const hasRole = (roles) => {
    if (!user) return false;
    // Handle backward compatibility: backoffice = manager
    const userRole = user.role;
    if (userRole === 'backoffice' && roles.includes('manager')) return true;
    if (userRole === 'manager' && roles.includes('backoffice')) return true;
    return roles.includes(userRole);
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions) => {
    if (!user || !user.permissions) return false;
    return permissions.some(p => user.permissions.includes(p));
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissions) => {
    if (!user || !user.permissions) return false;
    return permissions.every(p => user.permissions.includes(p));
  };

  // Check if user is admin
  const isAdmin = () => {
    return hasRole(['admin']);
  };

  // Propriétaire(s) du produit — accès exclusif à certaines pages (ex. « Flux d'activité »).
  // Surchargeable via REACT_APP_OWNER_EMAILS (séparées par des virgules).
  const OWNER_EMAILS = (process.env.REACT_APP_OWNER_EMAILS || 'yzarka@ldwsgroupe.com')
    .split(',').map((e) => e.trim().toLowerCase()).filter(Boolean);
  const isOwner = () => !!user && OWNER_EMAILS.includes(String(user.email || '').trim().toLowerCase());

  // Check if user can write (manager level)
  const canWrite = () => {
    return hasRole(['admin', 'manager', 'backoffice']);
  };

  // Check if user is technician or above
  const isTechnician = () => {
    return hasRole(['admin', 'manager', 'backoffice', 'technicien']);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    token: localStorage.getItem('token'),  // Expose token for WebSocket connections
    login,
    register,
    logout,
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isOwner,
    canWrite,
    isTechnician,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
